import { useEffect, useMemo, useState } from "react";
import { Job } from "../../../models/Job";
import { fetchJobsWithPagination } from "../../../services/jobService";

export interface RegisterData {
  jobs: Job[];
  total: number;
  institutions: number;
  fieldsCovered: number;
  loading: boolean;
  error: string | null;
}

/* One request serves the whole page: the opening entries, the annotated
   entry, the field index, and every figure quoted in the margin. Counts are
   whatever the register actually holds - they read truthfully at three
   entries and at three thousand. */
const WINDOW = 200;

export function useRegister(): RegisterData {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchJobsWithPagination({ page: 1, limit: WINDOW })
      .then((res) => {
        if (!alive) return;
        setJobs(res.jobs || []);
        setTotal(res.pagination?.totalJobs ?? (res.jobs || []).length);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError("The register could not be reached.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const institutions = useMemo(
    () => new Set(jobs.map((j) => (j.company || "").trim()).filter(Boolean)).size,
    [jobs]
  );

  const fieldsCovered = useMemo(
    () => new Set(jobs.map((j) => j.subjectCategory).filter(Boolean) as string[]).size,
    [jobs]
  );

  return { jobs, total, institutions, fieldsCovered, loading, error };
}

export const shortDate = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
